import {
  Rocket,
  Home,
  Map,
  ClipboardList,
  CheckCircle2,
  BarChart3,
  Users,
  Flame,
  TrendingDown,
  MessageCircle,
  Target,
  Radio,
  Zap,
  Wrench,
  Route,
  MapPin,
  MessageSquare,
  MessageSquareText,
  Scale,
  Mic,
  FileText,
  BookOpen,
  GitCompare,
  Crosshair,
  Send,
  Mail,
  Megaphone,
  Phone,
  Inbox,
  Settings as SettingsIcon,
  UserCog,
  UserPlus,
  UserSquare2,
  type LucideIcon,
} from 'lucide-react';
import { roleMatches } from '@/lib/roles';

export type NavCategory = 'field-ops' | 'intelligence' | 'planning' | 'admin' | 'geography';

export interface NavItem {
  key: string;
  label: string;
  Icon: LucideIcon;
  category: NavCategory;
  description?: string;
  roles?: string[];
}

export interface ContextualLink {
  fromView: string;
  relatedViews: string[];
}

/**
 * Central navigation configuration
 * Single source of truth for all menu items, categories, and contextual links
 */
const NAV_ITEMS: NavItem[] = [
  // Home (always first). The role-aware launcher: quick actions + the full
  // toolbox as a searchable grid. Client-side composition only — every tile
  // it renders is filtered through getVisibleItems, so it can never offer a
  // door the endpoint gates would slam.
  {
    key: 'home',
    label: 'Home',
    Icon: Home,
    category: 'geography',
    description: 'Your tools and quick actions, all in one place',
  },

  // Geography
  {
    key: 'map',
    label: 'County Map',
    Icon: Map,
    category: 'geography',
    description: 'Interactive county map with districts and precincts',
  },

  // Field Ops
  {
    key: 'canvass',
    label: 'Canvass',
    Icon: ClipboardList,
    category: 'field-ops',
    description: 'Start or view canvassing activities',
  },
  {
    key: 'canvass-review',
    label: 'Canvass Review',
    Icon: CheckCircle2,
    category: 'field-ops',
    description: 'Review and validate canvassing data',
  },
  {
    key: 'activity',
    label: 'Activity',
    Icon: BarChart3,
    category: 'field-ops',
    description: 'Real-time activity dashboard',
  },
  {
    key: 'messenger-queue',
    label: 'Send Texts',
    Icon: Send,
    category: 'field-ops',
    description: 'Click-by-click P2P SMS sending queue (TCPA-compliant)',
    roles: ['admin', 'org_manager', 'manager', 'sms_manager', 'messenger'],
  },
  {
    key: 'my-staff',
    label: 'My Volunteers',
    Icon: UserPlus,
    category: 'field-ops',
    description: 'Add field volunteers scoped to your turf',
    // Candidate-only self-service provisioning (outreach MVP). Explicit `roles`
    // puts this on the deterministic path (roleMap is ignored for an item that
    // carries its own roles), so ONLY a candidate sees it — no other role's
    // item count changes. POST /candidates/{id}/staff is candidate-gated
    // server-side; this must stay a subset of that.
    roles: ['candidate'],
  },

  // Intelligence
  {
    key: 'voters',
    label: 'Voters',
    Icon: Users,
    category: 'intelligence',
    description: 'Voter database and targeting',
  },
  {
    key: 'heatmap',
    label: 'Heatmap',
    Icon: Flame,
    category: 'intelligence',
    description: 'Voter density and engagement heatmap',
  },
  {
    key: 'dropoff',
    label: 'Dropoff',
    Icon: TrendingDown,
    category: 'intelligence',
    description: 'Voter dropoff analysis and trends',
  },
  {
    key: 'engagement',
    label: 'Engagement',
    Icon: MessageCircle,
    category: 'intelligence',
    description: 'Voter engagement metrics',
  },
  {
    key: 'r-lean',
    label: 'R-Lean',
    Icon: Target,
    category: 'intelligence',
    description: 'Republican-leaning voter identification',
  },
  {
    key: 'coverage',
    label: 'Coverage',
    Icon: Radio,
    category: 'intelligence',
    description: 'Canvassing coverage and gaps',
    // Backed by GET /optimizer/coverage (+ /optimizer/subregions for the
    // filter). Both were COORDINATOR_PLUS_ROLES until 2026-08-21 while this
    // menu offered the item to viewer / analyst / candidate — the endpoints
    // moved to ANALYTICS_READ_ROLES to match, because the rollup is a
    // read-only precinct aggregate with no route identity and no PII, and
    // /coverage now applies region_analytics_filter so the wider gate is not
    // a wider turf. The "plan routes for the worst N" action inside
    // CoverageView keeps the narrower ROUTE_WRITE_ROLES gate of its own.
  },
  {
    key: 'power-map',
    label: 'Power Map',
    Icon: Zap,
    category: 'intelligence',
    description: 'Power map visualization and analysis',
  },
  {
    key: 'turnout-accuracy',
    label: 'Model Accuracy',
    Icon: Crosshair,
    category: 'intelligence',
    description: 'Predicted turnout vs certified election results',
  },
  {
    key: 'my-responses',
    label: 'My Responses',
    Icon: MessageSquareText,
    category: 'intelligence',
    description: 'Voter responses for your race',
    // Candidate-only (candidate-attribution Phase 3). Explicit `roles` puts this
    // on the deterministic path (roleMap is ignored for an item carrying its own
    // roles), so ONLY a candidate sees it — no other role's item count changes,
    // and the NavConfig count tests stay green. GET /candidates/{id}/leans is
    // candidate-scoped server-side; this must stay a subset of that.
    roles: ['candidate'],
  },

  // Planning
  {
    key: 'optimizer',
    label: 'Optimizer',
    Icon: Wrench,
    category: 'planning',
    description: 'Route and activity optimization',
  },
  {
    key: 'routes-list',
    label: 'Routes',
    Icon: Route,
    category: 'planning',
    description: 'List and manage canvassing routes',
  },
  {
    key: 'routes',
    label: 'Route Detail',
    Icon: MapPin,
    category: 'planning',
    description: 'View and edit individual routes',
  },
  {
    key: 'feedback',
    label: 'Feedback',
    Icon: MessageSquare,
    category: 'planning',
    description: 'Canvasser feedback and notes',
  },
  {
    key: 'mail-builder',
    label: 'Mail List Builder',
    Icon: Mail,
    category: 'planning',
    description: 'Build tiered direct-mail household audiences with cost preview and CSV export',
    // Explicit `roles` puts this item on the deterministic path (an item
    // carrying its own roles ignores the roleMap entirely). Preview is gated
    // {analyst, admin, org_manager, manager, sms_manager} server-side;
    // this menu subset stays inside that (manager sees it via roleMap-less
    // explicit roles too — kept to the audience-design personas).
    roles: ['admin', 'org_manager', 'sms_manager', 'analyst'],
  },
  {
    key: 'channel-planner',
    label: 'Channel Planner',
    Icon: GitCompare,
    category: 'planning',
    description: 'Compare channels by cost per expected vote',
    // Same explicit `roles` set as `mail-builder` (same domain, same server
    // gate: /outreach/* preview endpoints are {analyst, admin, org_manager,
    // manager, sms_manager}). Carrying its own roles puts it on the
    // deterministic path, so no roleMap-driven role's item count changes —
    // it adds only to analyst (+1) and admin (+1), exactly like mail-builder.
    roles: ['admin', 'org_manager', 'sms_manager', 'analyst'],
  },

  // Admin
  {
    key: 'campaign-issues',
    label: 'Campaign Issues',
    Icon: Scale,
    category: 'admin',
    description: 'Campaign issue management',
  },
  {
    key: 'talking-points',
    label: 'Talking Points',
    Icon: Mic,
    category: 'admin',
    description: 'Campaign messaging and talking points',
  },
  {
    key: 'scripts',
    label: 'Scripts',
    Icon: FileText,
    category: 'admin',
    description: 'Canvasser scripts and templates',
  },
  {
    key: 'glossary',
    label: 'Glossary',
    Icon: BookOpen,
    category: 'admin',
    description: 'Terms and definitions reference',
  },
  {
    key: 'campaigns',
    label: 'Campaigns',
    Icon: Megaphone,
    category: 'admin',
    description: 'Batch SMS and P2P voter messaging campaigns',
    roles: ['admin', 'org_manager', 'manager', 'sms_manager'],
  },
  {
    key: 'messaging-numbers',
    label: '10DLC Numbers',
    Icon: Phone,
    category: 'admin',
    description: 'Manage the SMS long-code phone-number pool',
    roles: ['admin'],
  },
  {
    key: 'data-changes',
    label: 'Data Changes',
    Icon: GitCompare,
    category: 'admin',
    description: 'How the voter roll changed since the last import',
    roles: ['admin'],
  },
  {
    key: 'leads',
    label: 'Access Requests',
    Icon: UserPlus,
    category: 'admin',
    description: 'Inbound leads from the voterra.io marketing site',
    roles: ['admin'],
  },
  {
    key: 'enrichment-reconcile',
    label: 'Enrichment Reconcile',
    Icon: Inbox,
    category: 'admin',
    description: 'Resolve ambiguous & unmatched vendor enrichment rows',
    roles: ['admin'],
  },
  {
    key: 'candidates',
    label: 'Candidates',
    Icon: UserSquare2,
    category: 'admin',
    description: 'The people a door knock is for — name, office, and district reach',
    // Explicit `roles` puts this item on the deterministic path at :307-312
    // (an item carrying its own roles ignores the roleMap entirely). Must stay
    // a subset of what the endpoints permit: POST/PATCH /candidates is gated
    // {ADMIN, ORG_MANAGER}, so those two and no more.
    roles: ['admin', 'org_manager'],
  },
  {
    key: 'my-profile',
    label: 'My Profile',
    Icon: UserSquare2,
    category: 'admin',
    description: 'Your photo, bio, and links — the things voters see',
    // Candidate-only self-service, third of its kind after `my-staff` and
    // `my-responses` and gated the same deterministic way: an item carrying its
    // own `roles` ignores the roleMap entirely, so ONLY a candidate sees it and
    // no other role's item count moves. PATCH /candidates/{id}/profile is
    // CANDIDATE-gated server-side AND requires a relation='candidate' link to
    // that exact id; this must stay a subset of that.
    //
    // Not offered to admin/org_manager even though they CAN upload a headshot
    // for a candidate — their door is the Candidates admin view, which edits
    // every candidate in the tenant rather than "mine". A staff member landing
    // on a view titled "My Profile" with no candidate of their own would get
    // the empty state, not a useful surface.
    roles: ['candidate'],
  },
  {
    key: 'campaign-setup',
    label: 'Campaign Setup',
    Icon: Rocket,
    category: 'admin',
    description: 'Stand up a candidate and their whole team in one go',
    // Conservative on purpose. /admin/launch also admits anyone holding
    // `can_provision`, but that is per-account state the nav cannot see
    // without a fetch — so the menu shows the roles that always qualify and
    // the endpoint remains the authority.
    roles: ['admin', 'org_manager'],
  },
  {
    key: 'users',
    label: 'Users',
    Icon: UserCog,
    category: 'admin',
    description: 'Manage tenant members, roles, and region scopes',
    roles: ['admin', 'org_manager'],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    Icon: Scale,
    category: 'admin',
    description: 'Contribution limits, expenditures, and filing export',
    // Explicit roles → only these see it; adds 1 to admin's count only.
    roles: ['admin', 'org_manager', 'sms_manager'],
  },
  {
    key: 'settings',
    label: 'Settings',
    Icon: SettingsIcon,
    category: 'admin',
    description: 'Tenant brand & your theme preference',
    roles: ['admin'],
  },
];

const CONTEXTUAL_LINKS: ContextualLink[] = [
  { fromView: 'map', relatedViews: ['voters', 'coverage', 'optimizer', 'canvass', 'activity'] },
  { fromView: 'coverage', relatedViews: ['optimizer', 'power-map', 'voters'] },
  { fromView: 'activity', relatedViews: ['canvass-review', 'canvass'] },
  { fromView: 'canvass-review', relatedViews: ['activity', 'canvass'] },
  { fromView: 'optimizer', relatedViews: ['coverage', 'routes-list'] },
  { fromView: 'voters', relatedViews: ['heatmap', 'dropoff', 'r-lean'] },
  { fromView: 'heatmap', relatedViews: ['dropoff', 'voters', 'engagement'] },
  { fromView: 'dropoff', relatedViews: ['heatmap', 'voters'] },
  { fromView: 'power-map', relatedViews: ['coverage', 'voters'] },
  { fromView: 'routes-list', relatedViews: ['optimizer', 'routes'] },
  { fromView: 'r-lean', relatedViews: ['voters', 'optimizer'] },
  { fromView: 'turnout-accuracy', relatedViews: ['heatmap', 'power-map'] },
  { fromView: 'engagement', relatedViews: ['heatmap', 'activity'] },
];

// ---------------------------------------------------------------------------
// Menu ↔ endpoint agreement
// ---------------------------------------------------------------------------
//
// The endpoint is the authority and the nav is a hint (easy-buttons.md §2), but
// a hint that points at a 403 is worse than no hint: the user sees a menu item,
// clicks it, and gets a wall. That bug has now been paid for three times — the
// SMS_MANAGER lockout after the 0036 rename, `coverage` for three roles at
// once, and `analyst` on the routes surfaces. The rule that prevents the fourth:
//
//   **a role in a menu item's audience must be in the role set the endpoint
//   behind that item enforces.**
//
// The role sets below MIRROR the backend frozensets in
// `backend/app/models/user.py` — same convention as `ROUTE_WRITE_ROLES` in
// `src/api/routes.ts`, and the same warning applies: this is a mirror, so a
// change on either side has to be made on both. Lowercased here because that is
// how `/auth/me` sends a role and how `getVisibleItems` compares them.

/** models/user.py::ANALYTICS_READ_ROLES — aggregate analytics, no row PII. */
const ANALYTICS_READ_ROLES = [
  'admin', 'org_manager', 'manager', 'analyst', 'coordinator',
  'viewer', 'sms_manager', 'messenger', 'candidate',
] as const;

/** models/user.py::PII_READ_ROLES — individual voter rows. */
const PII_READ_ROLES = [
  'admin', 'org_manager', 'manager', 'coordinator', 'canvasser',
  'sms_manager', 'messenger', 'candidate',
] as const;

/** models/user.py::COORDINATOR_PLUS_ROLES — field-ops managerial ladder. */
const COORDINATOR_PLUS_ROLES = [
  'admin', 'org_manager', 'manager', 'coordinator',
] as const;

/** routers/canvass.py::CANVASS_FIELD_ROLES — anyone who walks doors, and up. */
const CANVASS_FIELD_ROLES = [
  'admin', 'org_manager', 'manager', 'coordinator', 'canvasser',
] as const;

/** routers/routes.py::ROUTE_READ_ROLES — managers + door knockers. Not analyst. */
const ROUTE_READ_ROLES = [
  'admin', 'org_manager', 'manager', 'coordinator', 'canvasser',
] as const;

/** models/user.py::MESSAGING_P2P_ROLES — click-by-click send. */
const MESSAGING_P2P_ROLES = [
  'admin', 'org_manager', 'manager', 'sms_manager', 'messenger',
] as const;

/** models/user.py::MESSAGING_BATCH_ROLES — TCPA batch send. No messenger. */
const MESSAGING_BATCH_ROLES = [
  'admin', 'org_manager', 'manager', 'sms_manager',
] as const;

/** models/user.py::USER_PROVISION_ROLES — create/manage accounts + candidates. */
const USER_PROVISION_ROLES = ['admin', 'org_manager'] as const;

/** models/user.py::USER_ADMIN_ROLES — tenant-wide administration. */
const ADMIN_ONLY = ['admin'] as const;

/** require_role(UserRole.CANDIDATE) — the candidate's own self-service surface. */
const CANDIDATE_ONLY = ['candidate'] as const;

export interface NavGate {
  /** The call the view makes on arrival, or the one that IS the feature. */
  endpoint: string;
  /** Roles that call admits, or `null` for "authenticated, no role gate". */
  roles: readonly string[] | null;
}

/**
 * The endpoint gate behind every menu item, keyed by nav key.
 *
 * Exhaustive by design and asserted to be exhaustive in the tests: adding a
 * NAV_ITEM without classifying it fails the suite. That is the point — the
 * classification is the moment you notice you are about to offer a menu item
 * to a role the server will refuse.
 *
 * `endpoint` names the call that decides whether the item works at all — the
 * one the view fires on mount, or the one the feature exists to make. A view
 * that additionally offers a NARROWER action (CoverageView's "plan routes",
 * ComplianceView's write forms) gates that button separately at its own call
 * site; this table is about whether the door opens, not what is behind it.
 */
export const NAV_ENDPOINT_GATES: Record<string, NavGate> = {
  // Home — client-side composition. It fires no calls of its own; every tile
  // it shows is one of THESE items filtered through getVisibleItems, so each
  // tile's gate is the entry for that tile's key.
  home: { endpoint: '(none — client-side composition)', roles: null },

  // Geography
  map: { endpoint: 'GET /voters/by-precinct', roles: ANALYTICS_READ_ROLES },

  // Field ops
  canvass: { endpoint: 'GET /canvass/shifts', roles: CANVASS_FIELD_ROLES },
  'canvass-review': { endpoint: 'GET /canvass/aars', roles: COORDINATOR_PLUS_ROLES },
  activity: { endpoint: 'GET /canvass/knocks', roles: COORDINATOR_PLUS_ROLES },
  'messenger-queue': { endpoint: 'POST /api/messaging/p2p/send', roles: MESSAGING_P2P_ROLES },
  'my-staff': { endpoint: 'POST /candidates/{id}/staff', roles: CANDIDATE_ONLY },

  // Intelligence
  voters: { endpoint: 'GET /voters', roles: PII_READ_ROLES },
  heatmap: { endpoint: 'GET /voters/analytics/turnout-heatmap', roles: ANALYTICS_READ_ROLES },
  dropoff: { endpoint: 'GET /voters/analytics/drop-off', roles: ANALYTICS_READ_ROLES },
  engagement: { endpoint: 'GET /narrative/* engagement rollups', roles: ANALYTICS_READ_ROLES },
  'r-lean': { endpoint: 'GET /voters/by-precinct', roles: ANALYTICS_READ_ROLES },
  coverage: { endpoint: 'GET /optimizer/coverage', roles: ANALYTICS_READ_ROLES },
  'power-map': { endpoint: 'GET /geo/power-map', roles: ANALYTICS_READ_ROLES },
  'turnout-accuracy': { endpoint: 'GET /predictions/turnout-accuracy', roles: ANALYTICS_READ_ROLES },
  'my-responses': { endpoint: 'GET /candidates/{id}/leans', roles: ANALYTICS_READ_ROLES },

  // Planning
  optimizer: { endpoint: 'POST /optimizer/plan', roles: COORDINATOR_PLUS_ROLES },
  'routes-list': { endpoint: 'GET /routes', roles: ROUTE_READ_ROLES },
  routes: { endpoint: 'GET /routes/{id}', roles: ROUTE_READ_ROLES },
  feedback: { endpoint: 'GET /canvass/aars', roles: COORDINATOR_PLUS_ROLES },
  'mail-builder': { endpoint: 'POST /outreach/mail-list/preview', roles: ANALYTICS_READ_ROLES },
  'channel-planner': { endpoint: 'POST /outreach/cost-per-vote', roles: ANALYTICS_READ_ROLES },

  // Admin
  'campaign-issues': { endpoint: 'GET /canvass/issues', roles: CANVASS_FIELD_ROLES },
  'talking-points': { endpoint: 'GET /canvass/talking-points', roles: CANVASS_FIELD_ROLES },
  scripts: { endpoint: 'GET /candidates', roles: ANALYTICS_READ_ROLES },
  // Static reference copy shipped with the bundle; nothing to gate.
  glossary: { endpoint: '(none — client-side content)', roles: null },
  campaigns: { endpoint: 'GET /api/messaging/batch/campaigns', roles: MESSAGING_BATCH_ROLES },
  'messaging-numbers': { endpoint: 'GET /admin/messaging/numbers', roles: ADMIN_ONLY },
  'data-changes': { endpoint: 'GET /admin/data-changes/summary', roles: ADMIN_ONLY },
  leads: { endpoint: 'GET /admin/access-requests', roles: ADMIN_ONLY },
  'enrichment-reconcile': { endpoint: 'GET /admin/enrichment/pending', roles: ADMIN_ONLY },
  candidates: { endpoint: 'POST /candidates', roles: USER_PROVISION_ROLES },
  // The write that IS this view. The headshot endpoints it also calls admit
  // {ADMIN, ORG_MANAGER, CANDIDATE}, i.e. strictly more — so the profile PATCH
  // is the narrower gate and therefore the one that decides whether the door
  // opens at all.
  'my-profile': { endpoint: 'PATCH /candidates/{id}/profile', roles: CANDIDATE_ONLY },
  // /admin/launch also admits any account holding `can_provision`, which the
  // nav cannot see without a fetch — so the menu lists the roles that ALWAYS
  // qualify. Conservative in the menu, authoritative at the endpoint.
  'campaign-setup': { endpoint: 'POST /admin/launch', roles: USER_PROVISION_ROLES },
  users: { endpoint: 'GET /admin/users', roles: USER_PROVISION_ROLES },
  compliance: { endpoint: 'GET /compliance/limits', roles: ANALYTICS_READ_ROLES },
  settings: { endpoint: 'PATCH /tenant/settings', roles: ADMIN_ONLY },
};

/**
 * The mismatches that are knowingly still here, keyed `${role}:${navKey}`.
 *
 * Not a suppression list — a ledger. The tests assert every entry is STILL a
 * real mismatch, so an entry that gets fixed on the server has to be deleted
 * here or the suite fails. Both current entries are CANVASSER against
 * ANALYTICS_READ_ROLES, and neither is fixable from this file: the honest
 * repairs are a server-side decision (does a door knocker get county-wide
 * aggregates?) rather than a menu edit.
 */
/**
 * Which view a role lands on — at login, after a session expiry, and wherever
 * a "back" walks all the way out.
 *
 * Everyone used to land on the county map. For a CANVASSER that was wrong
 * twice over: the map's data calls (`/voters/by-precinct`, `/voters/stats`)
 * are ANALYTICS_READ_ROLES and 403 for them, so the frame rendered without its
 * stats — and more basically, a door knocker's home should be the doors they
 * were given, not a county turnout heatmap they have no use for.
 *
 * Fixing it by widening the role set was the alternative and is the worse
 * repair: both endpoints DO region-filter, so a *scoped* canvasser would only
 * ever have seen their own turf, but an UNSCOPED one — `region_analytics_filter`
 * returns no filter when a user has zero scopes — would have seen the county.
 * Changing the destination removes the mismatch instead of widening a gate.
 */
export function homeViewForRole(role: string | null | undefined): string {
  // Array, not a bare string: `roleMatches` takes an Iterable<string>, and a
  // string is one — passing 'CANVASSER' type-checks and then silently compares
  // against each CHARACTER, matching nothing.
  //
  // Everyone else lands on the Home launcher (2026-08-24): the county map is
  // a report, not a starting point, and the real-slate field test said the
  // tools were buried. The map is one tap away in the bottom nav.
  return roleMatches(role, ['CANVASSER']) ? 'canvass' : 'home';
}

export const KNOWN_NAV_GATE_GAPS: Record<string, string> = {
  // Intentionally empty, and worth keeping rather than deleting.
  //
  // As of 2026-08-21 every nav item every role can see is one whose endpoint
  // will actually serve them — the `canvasser:map` and `canvasser:activity`
  // waivers were both closed by changing the app rather than by widening a
  // gate. An empty ledger is the goal state, not dead code: the suite asserts
  // each entry here is still a LIVE mismatch, so a fixed one fails until it is
  // deleted, and that only works if the mechanism survives reaching zero.
  //
  // Add an entry ONLY for a mismatch you are deliberately choosing to live
  // with, with the reason. The general menu-vs-endpoint test fails otherwise,
  // which is the point.
};

/**
 * Which roles each menu item is offered to, for the items that do NOT carry
 * their own `roles` array. Hoisted to module scope so the tests can enumerate
 * the roles the app actually knows about (`NAV_ROLES`) instead of hard-coding
 * a list that goes stale the next time a role is added.
 */
const ROLE_MAP: Record<string, string[]> = {
  // CANVASSER is in PII_READ_ROLES (so the route-filtered `voters` list works)
  // but NOT in ANALYTICS_READ_ROLES / COORDINATOR_PLUS_ROLES. `coverage`
  // (/optimizer/coverage) and `canvass-review` (/canvass/aars) had no
  // canvasser-appropriate data, so they stay out of the menu entirely.
  // `voters` stays but VotersView drops its analytics chrome for canvassers
  // (see there). `map` and `activity` were both removed 2026-08-21. The county
  // map was the app home for every role, but its stats 403 for a canvasser and
  // a door knocker's home should be the doors they were given — see
  // `homeViewForRole`. `activity` was kept as a signpost while its endpoint
  // 403'd, on the stated condition that it go once a canvasser-scoped "my
  // activity" rollup existed; CanvassView's home now lists "Your recent
  // shifts", so it has. A canvasser now has NO waived nav items: every entry
  // they can see is one they can load.
  // No 'home' for canvasser: their home IS their doors (homeViewForRole), and
  // CanvassView's own home screen already lists their shifts — a launcher
  // between a door knocker and their doors would be a step backward.
  canvasser: ['canvass', 'voters', 'glossary'],
  // viewer/analyst are in ANALYTICS_READ_ROLES but not PII_READ_ROLES, so
  // they can see aggregate views but not the individual-voter list.
  viewer: ['home', 'map', 'heatmap', 'dropoff', 'engagement', 'r-lean', 'coverage', 'power-map', 'turnout-accuracy', 'glossary'],
  // ANALYST is an analytics role, not a field role. `optimizer`,
  // `routes-list`, `routes` and `feedback` were removed 2026-08-21: every one
  // of them 403'd. /optimizer/plan is COORDINATOR_PLUS_ROLES, /canvass/aars is
  // COORDINATOR_PLUS_ROLES, and ROUTE_READ_ROLES excludes ANALYST *on purpose*
  // — an analyst studies turf, they do not read or plan walk-lists. The fix
  // for those four was to trim the menu, never to widen a gate; `coverage`
  // went the other way (see the note on it below) because it is a read-only
  // precinct aggregate that ANALYTICS_READ_ROLES already describes.
  analyst: ['home', 'map', 'heatmap', 'dropoff', 'engagement', 'r-lean', 'coverage', 'power-map', 'turnout-accuracy', 'glossary'],
  // New SMS-only role: MESSENGER. Can do P2P send + see voters; nothing else.
  messenger: ['home', 'map', 'messenger-queue', 'voters', 'glossary'],
  // CAMPAIGN_MANAGER: messaging-focused, plus voter visibility for audience design.
  // `talking-points` was removed 2026-08-21 for the same reason as the analyst
  // trims: GET /canvass/talking-points is CANVASS_FIELD_ROLES (door knockers
  // and up) and SMS_MANAGER is not in it, so the item only ever 403'd. If the
  // briefing copy should be readable by the messaging roles, that is a
  // deliberate widening of the canvass read gate — make it there, then put the
  // item back; do not re-add the menu entry on its own.
  sms_manager: ['home', 'map', 'messenger-queue', 'campaigns', 'voters', 'heatmap', 'r-lean', 'glossary'],
  // CANDIDATE (candidate-attribution Phase 3): their own race, region-scoped to
  // their turf server-side. Sees the map + their voters + own-turf analytics;
  // NO admin, NO routes/optimizer, NO messaging, NO bulk export. An explicit
  // entry (not the fail-open default) so the role is bounded in the menu too.
  candidate: ['home', 'map', 'voters', 'heatmap', 'dropoff', 'engagement', 'r-lean', 'coverage', 'power-map', 'turnout-accuracy', 'glossary'],
  coordinator: NAV_ITEMS.map(item => item.key),
  // ORG_MANAGER/MANAGER are existing field-ops admin tiers; treat them like
  // coordinator + the new messaging surface (they're already in admin scope).
  org_manager: NAV_ITEMS.map(item => item.key),
  manager: NAV_ITEMS.map(item => item.key),
  admin: NAV_ITEMS.map(item => item.key),
};

/**
 * Every role this app knows how to build a menu for — the `ROLE_MAP` keys plus
 * the roles that only ever appear in an item's explicit `roles` array. Derived,
 * not typed out, so a new role cannot be added to the menu without the
 * menu-vs-endpoint test below picking it up.
 */
export const NAV_ROLES: string[] = Array.from(
  new Set([
    ...Object.keys(ROLE_MAP),
    ...NAV_ITEMS.flatMap(item => item.roles ?? []),
  ]),
).sort();

/**
 * Get visible nav items for a given user role
 */
export function getVisibleItems(role: string): NavItem[] {
  const allowedKeys = ROLE_MAP[role.toLowerCase()] || NAV_ITEMS.map(item => item.key);

  return NAV_ITEMS.filter(item => {
    if (item.roles && item.roles.length > 0) {
      return item.roles.includes(role.toLowerCase());
    }
    return allowedKeys.includes(item.key);
  });
}

/**
 * Get contextual navigation links for the current view
 */
export function getContextualLinks(currentView: string): NavItem[] {
  const contextLink = CONTEXTUAL_LINKS.find(link => link.fromView === currentView);
  if (!contextLink || contextLink.relatedViews.length === 0) {
    return [];
  }

  return contextLink.relatedViews
    .map(viewKey => NAV_ITEMS.find(item => item.key === viewKey))
    .filter((item): item is NavItem => item !== undefined);
}

// ---------------------------------------------------------------------------
// Bottom navigation (mobile primary nav)
// ---------------------------------------------------------------------------
//
// The hamburger drawer is SECONDARY navigation — the full catalog. A phone
// user's primary tools live in a fixed bottom bar: Home + up to three
// role-curated destinations + Menu (which opens the drawer). Five slots, all
// labeled, per the platform conventions (Material bottom-nav-limit / iOS tab
// bar). Curation below is intent, not permission: everything is intersected
// with getVisibleItems, so the menu-vs-endpoint suite covers these too.
const BOTTOM_NAV_PRIORITY: Record<string, string[]> = {
  admin: ['map', 'canvass', 'voters'],
  org_manager: ['map', 'canvass', 'voters'],
  manager: ['map', 'canvass', 'voters'],
  // Coordinators run the field week: routes over raw voter rows.
  coordinator: ['map', 'routes-list', 'canvass'],
  // Canvasser's Home slot already IS their doors (homeViewForRole → canvass),
  // so the extra slots are the two other things they can open.
  canvasser: ['voters', 'glossary'],
  viewer: ['map', 'heatmap', 'coverage'],
  analyst: ['map', 'heatmap', 'coverage'],
  sms_manager: ['messenger-queue', 'campaigns', 'voters'],
  messenger: ['messenger-queue', 'voters', 'map'],
  candidate: ['map', 'voters', 'my-responses'],
};

/**
 * The up-to-three role-curated destinations that sit between Home and Menu in
 * the mobile bottom bar. Always a subset of getVisibleItems(role) — an
 * uncurated (new/unknown) role falls back to its first visible items so the
 * bar never renders empty for a legitimate account.
 */
export function getBottomNavItems(role: string | null | undefined): NavItem[] {
  const normalized = (role ?? '').toLowerCase();
  const visible = getVisibleItems(normalized);
  const homeKey = homeViewForRole(normalized);

  const priority = BOTTOM_NAV_PRIORITY[normalized] ?? visible.map((i) => i.key);

  // NOTE: `Map` in this module is the lucide icon, not the collection — a
  // linear find over ≤33 items is fine and dodges the shadowing.
  const picked: NavItem[] = [];
  for (const key of priority) {
    if (picked.length >= 3) break;
    // Home occupies its own slot; never duplicate it or the role's home view.
    if (key === 'home' || key === homeKey) continue;
    const item = visible.find((i) => i.key === key);
    if (item) picked.push(item);
  }
  return picked;
}

/**
 * Category display order and metadata
 */
export const CATEGORY_ORDER: NavCategory[] = ['geography', 'field-ops', 'intelligence', 'planning', 'admin'];

export const CATEGORY_LABELS: Record<NavCategory, string> = {
  geography: 'Geography',
  'field-ops': 'Field Operations',
  intelligence: 'Intelligence',
  planning: 'Planning',
  admin: 'Administration',
};
